import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type { Company, UserAlarmSettings } from "@types"
import { AxiosError } from "axios"

// =====================
// Types
// =====================

/**
 * Réponse de l'API pour l'utilisateur connecté
 */
export type GetUserResponse = {
    _id_user: string
    firstname: string
    lastname: string
    username: string
    img?: string
    company?: Company
    multipleCompanies?: Company[]
    alarme?: { notifications: UserAlarmSettings }
    phone?: string
}

/**
 * Payload pour la mise à jour des informations utilisateur (partiel)
 */
export type UpdateCurrentUserPayload = Partial<{
    firstname: string
    lastname: string
    phone: string | null
    alarme: {
        notifications: UserAlarmSettings
    }
    last_login: {
        _id_company: string
    }
}>

/**
 * Réponse de la mise à jour des informations utilisateur
 */
export type UpdateCurrentUserResponse = {
    firstname: string
    lastname: string
    phone: string | null
    alarme?: {
        notifications: UserAlarmSettings
    }
}

/**
 * Résultat de recherche d'utilisateur par email
 */
export type UserSearchResult = {
    _id: string
    firstname: string
    lastname: string
    username: string
    phone?: string
    img?: string
    /**
     * Liste des entreprises associées à l'utilisateur
     */
    multiCompany?: CompanyUserAssociation[]
    company?: Company
}

/**
 * Association utilisateur-entreprise
 */
export type CompanyUserAssociation = {
    _id: string
    name: string
    idcompany?: string
    legalForm?: string
    address?: {
        street?: string
        zipcode?: string
        city?: string
        country?: string
        inline_name?: string
    }
}

/**
 * Utilisateur de l'entreprise
 */
export type CompanyUser = {
    _id: string
    firstname: string
    lastname: string
    username: string
    phone?: string
    img?: string
    type:
        | "employe"
        | "employe_read_only"
        | "administrateur"
        | "owner"
        | "client"
    company?: { name: string }
}

/**
 * Le service permettant de gérer les utilisateurs de l'application.
 */
export class UserService {
    /**
     * Récupère les informations sur le compte de l'utilisateur connecté
     * GET /api/metier/v5/user/me
     * @returns Les informations de l'utilisateur connecté
     */
    async getCurrentUser(): Promise<GetUserResponse> {
        return handleServiceCall<GetUserResponse>(async () => {
            const { data } = await axios.get("/api/metier/v5/user/me")
            return {
                _id_user: data?._id,
                username: data?.username || "",
                firstname: data?.firstname || "",
                lastname: data?.lastname || "",
                img: data?.img,
                company: data?.company,
                multipleCompanies: data?.multipleCompany,
                alarme: data?.alarme,
                phone: data?.phone,
            }
        })
    }

    /**
     * Met à jour les informations de l'utilisateur connecté.
     * PUT /api/metier/v5/user/me
     * Permet de mettre à jour les champs suivants de façon partielle :
     * - firstname : Prénom de l'utilisateur
     * - lastname : Nom de l'utilisateur
     * - phone : Numéro de téléphone de l'utilisateur
     * - alarme : Paramètres d'alarme de l'utilisateur
     * - last_login : Dernière connexion avec l'entreprise sélectionnée
     * @param payload - Les données à mettre à jour (tous les champs sont optionnels)
     * @returns Les informations mises à jour de l'utilisateur
     */
    async updateCurrentUser(
        payload: UpdateCurrentUserPayload
    ): Promise<UpdateCurrentUserResponse> {
        return handleServiceCall<UpdateCurrentUserResponse>(async () => {
            const { data } = await axios.put("/api/metier/v5/user/me", payload)
            return {
                firstname: data?.firstname || "",
                lastname: data?.lastname || "",
                phone: data?.phone || null,
                alarme: data?.alarme,
            }
        })
    }
    /**
     * Envoie un email de réinitialisation de mot de passe
     * @param username - Adresse email de l'utilisateur
     * @returns Promise<void>
     */
    async sendResetPasswordEmail(username: string): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.post("/api/metier/auth/user/reset-password", {
                username,
            })
        })
    }

    /**
     * Recherche un utilisateur par son email
     * GET /api/metier/v5/user
     * @param username - Email de l'utilisateur à rechercher
     * @returns L'utilisateur trouvé ou null
     */
    async getUserByUsername(
        username: string
    ): Promise<UserSearchResult | null> {
        return handleServiceCall<UserSearchResult | null>(async () => {
            try {
                const { data } = await axios.get<UserSearchResult>(
                    "/api/metier/v5/user",
                    {
                        params: {
                            username,
                            company: true,
                            multipleCompany: true,
                        },
                    }
                )
                if (!data) return null
                return data
            } catch (error) {
                const axiosError = error as AxiosError
                // Si l'utilisateur n'est pas trouvé, retourner null
                if (axiosError?.response?.status === 404) {
                    return null
                }
                throw error
            }
        })
    }

    /**
     * Récupère les utilisateurs de l'entreprise par type
     * GET /api/metier/company/user
     * @param types - Types d'utilisateurs à récupérer
     * @returns Liste des utilisateurs de l'entreprise
     */
    async getCompanyUsers(
        types: string[] = [
            "employe",
            "administrateur",
            "employe_read_only",
            "owner",
        ]
    ): Promise<CompanyUser[]> {
        return handleServiceCall<CompanyUser[]>(async () => {
            const {
                data: { data },
            } = await axios.get<{ data: CompanyUser[] }>(
                "/api/metier/company/user",
                {
                    params: { type: types as string[] },
                }
            )
            return data
        })
    }

    /**
     * Met à jour le rôle d'un utilisateur dans l'entreprise
     * PUT /api/metier/company/user
     * @param userId - Identifiant de l'utilisateur
     * @param role - Nouveau rôle de l'utilisateur (administrateur, employe, employe_read_only)
     * @returns Promise<void>
     */
    async updateCompanyUserRole(
        userId: string,
        role: "administrateur" | "employe" | "employe_read_only"
    ): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.put("/api/metier/company/user", {
                type: role,
                _id_user: userId,
            })
        })
    }

    /**
     * Supprime un utilisateur de l'entreprise
     * DELETE /api/metier/company/user
     * @param userId - Identifiant de l'utilisateur
     * @param role - Rôle de l'utilisateur à supprimer
     * @returns Promise<void>
     */
    async deleteCompanyUser(userId: string, role: string): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.delete("/api/metier/company/user", {
                data: {
                    _id_user: userId,
                    role,
                },
            })
        })
    }

    /**
     * Ajoute un utilisateur à l'entreprise
     * POST /api/metier/company/user
     * @param payload - Données de l'utilisateur à ajouter
     * @returns Promise<void>
     */
    async addCompanyUser(payload: {
        firstname: string
        lastname: string
        username: string
        type: "administrateur" | "employe" | "employe_read_only"
    }): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.post("/api/metier/company/user", {
                user: {
                    firstname: payload.firstname,
                    lastname: payload.lastname,
                    username: payload.username,
                },
                type: payload.type,
            })
        })
    }
}
